/**
 * placement-closure.ts - the Policenkontrolle + Abschluss contract (P2 Placement Slice 15).
 *
 * The closing chapter of the Offert-/Antragsprozess (Prozesskonzept P2 step 21, P1 V3.0 steps
 * 6/14/16): the received police is compared field by field against the Antrag (the pure
 * `berechnePolicenkontrolle`), the result (deckungsgleich/abweichung) and the structured delta are
 * persisted, the closure flips to erledigt, and the police is delivered to the customer. This module
 * is the SINGLE SOURCE OF TRUTH for the Kontrollfelder, the tolerances (Q143), the match-ladder and
 * trigger vocab, the Klaerung vocab (E8/E18), the Historie phase mapping (Tab 6), the Abschluss wire
 * shapes and every pinned user-facing string.
 *
 * Pure module - no Date, no side effects. All date math is string-based (TZ-free). ASCII hyphens only
 * (no en/em dash; the api/web lint forbids them in shared/web literals).
 *
 * [Source: docs/product/p2-slices/slice-15-policenkontrolle-abschluss-design.md §3.1 (D4/D8)]
 */

import type { PlacementClosureStatus, PlacementComparisonResult, PlacementStatus } from './placement';
import type { ApplicationStatus } from './application';

// ── Trigger / match-art / field vocab ─────────────────────────────────────────────

export const POLICENKONTROLLE_AUSLOESER = { P1_INGEST: 'p1_ingest', P1_AKTIVIERUNG: 'p1_aktivierung', POLICE_ERHALTEN: 'police_erhalten', BROKER: 'broker' } as const;
export type PolicenkontrolleAusloeser = (typeof POLICENKONTROLLE_AUSLOESER)[keyof typeof POLICENKONTROLLE_AUSLOESER];
export const POLICENKONTROLLE_AUSLOESER_LABELS: Record<PolicenkontrolleAusloeser, string> = { p1_ingest: 'Policeneingang', p1_aktivierung: 'Aktivierung der Police', police_erhalten: 'Police erhalten (Underwriting)', broker: 'Manuell durch Broker' };

export const POLICENKONTROLLE_MATCH_ART = { POLICE_ERHALTEN_ID: 'police_erhalten_id', POLICENNUMMER: 'policennummer', PROMOTION_ZIEL: 'promotion_ziel', REFERENZPOLICE: 'referenzpolice', HEURISTIK: 'heuristik', MANUELL: 'manuell' } as const;
export type PolicenkontrolleMatchArt = (typeof POLICENKONTROLLE_MATCH_ART)[keyof typeof POLICENKONTROLLE_MATCH_ART];
export const POLICENKONTROLLE_MATCH_ART_LABELS: Record<PolicenkontrolleMatchArt, string> = { police_erhalten_id: 'Bereits verknuepfte Police', policennummer: 'Policennummer aus dem Underwriting', promotion_ziel: 'Promotion-Ziel der Kundenfreigabe', referenzpolice: 'Referenzpolice (Erneuerung)', heuristik: 'Versicherer, Kunde und Sparte', manuell: 'Manuell verknuepft' };

export const POLICENKONTROLLE_FELDER = ['versicherer', 'policennummer', 'versicherungsbeginn', 'vertragsablauf', 'praemie_jahr', 'versicherungssumme', 'selbstbehalt'] as const;
export type PolicenkontrolleFeld = (typeof POLICENKONTROLLE_FELDER)[number];
export const POLICENKONTROLLE_FELD_LABELS: Record<PolicenkontrolleFeld, string> = { versicherer: 'Versicherer', policennummer: 'Policennummer', versicherungsbeginn: 'Versicherungsbeginn', vertragsablauf: 'Vertragsablauf', praemie_jahr: 'Jahrespraemie', versicherungssumme: 'Versicherungssumme', selbstbehalt: 'Selbstbehalt' };

export const POLICENKONTROLLE_FELD_STATUS = { GLEICH: 'gleich', ABWEICHEND: 'abweichend', UNBEKANNT: 'unbekannt', INFO: 'info' } as const;
export type PolicenkontrolleFeldStatus = (typeof POLICENKONTROLLE_FELD_STATUS)[keyof typeof POLICENKONTROLLE_FELD_STATUS];
export const POLICENKONTROLLE_FELD_STATUS_LABELS: Record<PolicenkontrolleFeldStatus, string> = { gleich: 'Uebereinstimmend', abweichend: 'Abweichend', unbekannt: 'Nicht pruefbar', info: 'Information' };

export const KLAERUNG_STATUS = { NICHT_NOETIG: 'nicht_noetig', OFFEN: 'offen', GEKLAERT: 'geklaert' } as const;
export type KlaerungStatus = (typeof KLAERUNG_STATUS)[keyof typeof KLAERUNG_STATUS];
export const KLAERUNG_STATUS_LABELS: Record<KlaerungStatus, string> = { nicht_noetig: 'Keine Klaerung noetig', offen: 'Klaerung offen', geklaert: 'Geklaert' };

export const PLACEMENT_ABSCHLUSS_ART = { AUTOMATISCH: 'automatisch', MANUELL_FREIGEGEBEN: 'manuell_freigegeben' } as const;
export type PlacementAbschlussArt = (typeof PLACEMENT_ABSCHLUSS_ART)[keyof typeof PLACEMENT_ABSCHLUSS_ART];
export const PLACEMENT_ABSCHLUSS_ART_LABELS: Record<PlacementAbschlussArt, string> = { automatisch: 'Automatisch (deckungsgleich)', manuell_freigegeben: 'Nach geklaerter Abweichung freigegeben' };

export const POLICE_ZUSTELLUNG_CHANNEL_VALUES: readonly ('portal' | 'email')[] = ['portal', 'email'];

/** Tolerances (Q143). */
export const PK_PRAEMIE_TOLERANZ_CHF = 5;
export const PK_PRAEMIE_TOLERANZ_PROZENT = 1;
export const PK_ABLAUF_TOLERANZ_TAGE = 31;
export const PK_COVERAGE_KEYS: Record<'versicherungssumme' | 'selbstbehalt', readonly string[]> = { versicherungssumme: ['versicherungssumme', 'deckungssumme', 'sum_insured'], selbstbehalt: ['selbstbehalt', 'deductible'] };
export const PK_GRUND_MIN = 3;
export const PK_GRUND_MAX = 2000;
export const PK_HISTORIE_LIMIT_DEFAULT = 50;
export const PK_HISTORIE_LIMIT_MAX = 200;
export const PK_HISTORIE_KONTEXT_KEYS: readonly string[] = ['source', 'from', 'to', 'from_status', 'to_status', 'channel', 'runde_no', 'submission_number', 'carrier_uid', 'result', 'policy_id', 'match_art', 'ausloeser', 'kontroll_nr', 'abschluss_art', 'control_id', 'communication_id', 'empfaenger_domain'];

// ── the pure Policenkontrolle (D4) ─────────────────────────────────────────────────

export interface PolicenkontrolleAntragFacts { carrier_uid: string; police_erhalten_policy_number: string | null; begin_date: string; vertragsdauer_monate: number | null; praemie_jahr: number | null; versicherungssumme: number | null; selbstbehalt: number | null }
export interface PolicenkontrollePoliceFacts { policy_number: string; carrier_uid: string | null; inception_date: string | null; expiry_date: string | null; valid_from: string | null; premium_gross: number | null; premium_net: number | null; coverage_json: Record<string, unknown> | null }
export interface PolicenkontrolleZeile { feld: PolicenkontrolleFeld; label: string; soll: string | number | null; ist: string | number | null; status: PolicenkontrolleFeldStatus; toleranz: string | null; hinweis: string | null }
export interface PolicenkontrolleErgebnis { result: PlacementComparisonResult; zeilen: PolicenkontrolleZeile[]; abweichungen_count: number; unbekannt_count: number; vollstaendig: boolean }

const PAD2 = (n: number): string => String(n).padStart(2, '0');
const PAD4 = (n: number): string => String(n).padStart(4, '0');
const DAYS_IN_MONTH = (year: number, month1: number): number => {
  // month1 is 1-based; day 0 of next month = last day of this month, computed without local TZ.
  if (month1 === 2) return (year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)) ? 29 : 28;
  return [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month1 - 1];
};

/** 'YYYY-MM-DD' + n months, end-of-month clamped (2027-01-31 + 1 -> 2027-02-28). Pure string math. */
export function addMonthsIso(isoDate: string, months: number): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  const total = y * 12 + (m - 1) + months;
  const ny = Math.floor(total / 12);
  const nm0 = ((total % 12) + 12) % 12; // 0-based month, normalised
  const nd = Math.min(d, DAYS_IN_MONTH(ny, nm0 + 1));
  return `${PAD4(ny)}-${PAD2(nm0 + 1)}-${PAD2(nd)}`;
}

/** |a - b| in whole days via Date.UTC (no local TZ). */
export function diffTageIso(a: string, b: string): number {
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  return Math.abs(Math.round((Date.UTC(ay, am - 1, ad) - Date.UTC(by, bm - 1, bd)) / 86400000));
}

/** number or numeric string ("4'850.00", "CHF 4850"); first key that yields a finite number wins. */
export function readNumeric(obj: Record<string, unknown> | null, keys: readonly string[]): number | null {
  if (!obj) return null;
  for (const k of keys) {
    if (!(k in obj)) continue;
    const v = obj[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string') {
      const cleaned = v.replace(/chf/gi, '').replace(/['\s]/g, '').trim();
      if (cleaned === '') continue;
      const n = Number(cleaned);
      if (Number.isFinite(n)) return n;
    }
  }
  return null;
}

/** max(PK_PRAEMIE_TOLERANZ_CHF, soll * PK_PRAEMIE_TOLERANZ_PROZENT / 100). */
export function praemieToleranz(soll: number): number {
  return Math.max(PK_PRAEMIE_TOLERANZ_CHF, Math.abs(soll) * PK_PRAEMIE_TOLERANZ_PROZENT / 100);
}

const PK_ABLAUF_TOLERANZ_TEXT = `+/- ${PK_ABLAUF_TOLERANZ_TAGE} Tage`;
const PK_PRAEMIE_TOLERANZ_TEXT = `max. CHF ${PK_PRAEMIE_TOLERANZ_CHF} / ${PK_PRAEMIE_TOLERANZ_PROZENT} %`;

/**
 * D4 - the field-wise Antrag-vs-Police compare with the pinned absolute tolerances. A field absent on
 * either side is `unbekannt` and NEVER counts as an Abweichung (Q144). result = abweichung iff at least
 * one field is abweichend; vollstaendig iff no field is unbekannt.
 */
export function berechnePolicenkontrolle(antrag: PolicenkontrolleAntragFacts, police: PolicenkontrollePoliceFacts): PolicenkontrolleErgebnis {
  const zeilen: PolicenkontrolleZeile[] = [];

  // 1. Versicherer (exact; police carrier NULL -> unbekannt)
  {
    const ist = police.carrier_uid;
    const status: PolicenkontrolleFeldStatus = ist === null ? 'unbekannt' : ist === antrag.carrier_uid ? 'gleich' : 'abweichend';
    zeilen.push({ feld: 'versicherer', label: POLICENKONTROLLE_FELD_LABELS.versicherer, soll: antrag.carrier_uid, ist, status, toleranz: null, hinweis: ist === null ? 'Kein Versicherer in der Police hinterlegt' : null });
  }

  // 2. Policennummer (info when no typed number; a typed number that differs = the wrong police linked)
  {
    const soll = antrag.police_erhalten_policy_number;
    let status: PolicenkontrolleFeldStatus;
    let hinweis: string | null = null;
    if (soll === null) { status = 'info'; hinweis = 'Keine Policennummer im Antrag erfasst'; }
    else status = soll === police.policy_number ? 'gleich' : 'abweichend';
    zeilen.push({ feld: 'policennummer', label: POLICENKONTROLLE_FELD_LABELS.policennummer, soll, ist: police.policy_number, status, toleranz: null, hinweis });
  }

  // 3. Versicherungsbeginn (exact day; Q143)
  {
    const ist = police.inception_date ?? police.valid_from;
    const status: PolicenkontrolleFeldStatus = ist === null ? 'unbekannt' : ist === antrag.begin_date ? 'gleich' : 'abweichend';
    zeilen.push({ feld: 'versicherungsbeginn', label: POLICENKONTROLLE_FELD_LABELS.versicherungsbeginn, soll: antrag.begin_date, ist, status, toleranz: null, hinweis: null });
  }

  // 4. Vertragsablauf (+/- 31 Tage)
  {
    const sollAblauf = antrag.vertragsdauer_monate !== null ? addMonthsIso(antrag.begin_date, antrag.vertragsdauer_monate) : null;
    const ist = police.expiry_date;
    let status: PolicenkontrolleFeldStatus;
    if (sollAblauf === null || ist === null) status = 'unbekannt';
    else status = diffTageIso(sollAblauf, ist) <= PK_ABLAUF_TOLERANZ_TAGE ? 'gleich' : 'abweichend';
    zeilen.push({ feld: 'vertragsablauf', label: POLICENKONTROLLE_FELD_LABELS.vertragsablauf, soll: sollAblauf, ist, status, toleranz: PK_ABLAUF_TOLERANZ_TEXT, hinweis: antrag.vertragsdauer_monate === null ? 'Keine Vertragsdauer im Antrag erfasst' : null });
  }

  // 5. Jahrespraemie (max CHF 5 / 1%)
  {
    const soll = antrag.praemie_jahr;
    const ist = police.premium_gross ?? police.premium_net;
    const hinweis = ist === null ? null : police.premium_gross !== null ? 'Bruttopraemie der Police' : 'Nettopraemie der Police';
    let status: PolicenkontrolleFeldStatus;
    if (soll === null || ist === null) status = 'unbekannt';
    else status = Math.abs(soll - ist) <= praemieToleranz(soll) ? 'gleich' : 'abweichend';
    zeilen.push({ feld: 'praemie_jahr', label: POLICENKONTROLLE_FELD_LABELS.praemie_jahr, soll, ist, status, toleranz: PK_PRAEMIE_TOLERANZ_TEXT, hinweis });
  }

  // 6. Versicherungssumme (exact; from coverage_json)
  {
    const soll = antrag.versicherungssumme;
    const ist = readNumeric(police.coverage_json, PK_COVERAGE_KEYS.versicherungssumme);
    const status: PolicenkontrolleFeldStatus = soll === null || ist === null ? 'unbekannt' : soll === ist ? 'gleich' : 'abweichend';
    zeilen.push({ feld: 'versicherungssumme', label: POLICENKONTROLLE_FELD_LABELS.versicherungssumme, soll, ist, status, toleranz: null, hinweis: ist === null ? 'Nicht in der Police hinterlegt' : null });
  }

  // 7. Selbstbehalt (exact; from coverage_json)
  {
    const soll = antrag.selbstbehalt;
    const ist = readNumeric(police.coverage_json, PK_COVERAGE_KEYS.selbstbehalt);
    const status: PolicenkontrolleFeldStatus = soll === null || ist === null ? 'unbekannt' : soll === ist ? 'gleich' : 'abweichend';
    zeilen.push({ feld: 'selbstbehalt', label: POLICENKONTROLLE_FELD_LABELS.selbstbehalt, soll, ist, status, toleranz: null, hinweis: ist === null ? 'Nicht in der Police hinterlegt' : null });
  }

  const abweichungen_count = zeilen.filter((z) => z.status === 'abweichend').length;
  const unbekannt_count = zeilen.filter((z) => z.status === 'unbekannt').length;
  const result: PlacementComparisonResult = abweichungen_count > 0 ? 'abweichung' : 'deckungsgleich';
  return { result, zeilen, abweichungen_count, unbekannt_count, vollstaendig: unbekannt_count === 0 };
}

// ── Historie (D8) ──────────────────────────────────────────────────────────────────

export const PLACEMENT_HISTORIE_PHASE = { AUSSCHREIBUNG: 'ausschreibung', RISIKO: 'risiko', SUBMISSION: 'submission', QNA: 'qna', OFFERTEN: 'offerten', VERGLEICH: 'vergleich', ENTSCHEID: 'entscheid', ANTRAG: 'antrag', UNDERWRITING: 'underwriting', ABSCHLUSS: 'abschluss', SONSTIGES: 'sonstiges' } as const;
export type PlacementHistoriePhase = (typeof PLACEMENT_HISTORIE_PHASE)[keyof typeof PLACEMENT_HISTORIE_PHASE];
export const PLACEMENT_HISTORIE_PHASE_LABELS: Record<PlacementHistoriePhase, string> = { ausschreibung: 'Ausschreibung', risiko: 'Risikoprofil und Soll-Deckung', submission: 'Offertanfragen', qna: 'Rueckfragen', offerten: 'Offerten', vergleich: 'Vergleich und Empfehlung', entscheid: 'Kundenfreigabe und Entscheid', antrag: 'Antrag', underwriting: 'Underwriting', abschluss: 'Policenkontrolle und Abschluss', sonstiges: 'Sonstiges' };

/**
 * Pure: the Tab-6 phase from an activity event name (dot style) or an audit action code (underscore
 * style). The order matters: the more specific abschluss / underwriting / risiko rules win before the
 * generic placement.* / application.* prefixes. See the D8 prefix table.
 */
export function placementHistoriePhase(eventOrAction: string): PlacementHistoriePhase {
  const s = eventOrAction;
  const P = PLACEMENT_HISTORIE_PHASE;

  // underwriting - the E6/E7 rounds, Deckung, Police erhalten (dot) + the audit markers.
  if (
    /^application\.(accepted|rejected|condition_|alternative_|fallback_|coverage_confirmed|police_received)/.test(s) ||
    (s.startsWith('application_') && /(_uw_|_condition_|_alternative_|_fallback_|_coverage_|_police_)/.test(s))
  ) {
    return P.UNDERWRITING;
  }

  // abschluss - Policenkontrolle + Abschluss (Slice 15).
  if (
    s === 'application.controlled' || s === 'application.completed' ||
    s === 'application_control' || s === 'application_complete' ||
    s.startsWith('policy.control.') ||
    s.startsWith('placement.policy') || s.startsWith('placement_policy') ||
    s === 'placement.closed' || s === 'placement_close' ||
    s === 'placement_shell_replace' || s === 'placement_reconcile_conflict'
  ) {
    return P.ABSCHLUSS;
  }

  // risiko - Risikoprofil, Soll-Deckung, Schadenverlauf.
  if (
    s.startsWith('placement.snapshot_') || s.startsWith('placement_snapshot_') ||
    s.startsWith('placement.risk_profile_') || s.startsWith('placement_risk_profile_') ||
    s.startsWith('placement.step2_') || s.startsWith('placement_step2_') ||
    s.startsWith('placement.pflichtdeckung_') || s.startsWith('placement_pflichtdeckung_') ||
    s === 'policy.claims_history_retrieved' || s === 'policy_claims_history_retrieved'
  ) {
    return P.RISIKO;
  }

  if (s.startsWith('submission.') || s.startsWith('submission_')) return P.SUBMISSION;
  if (s.startsWith('qna.') || s.startsWith('qna_')) return P.QNA;
  if (s.startsWith('placement.offer.') || s.startsWith('offer.') || s.startsWith('offer_')) return P.OFFERTEN;
  if (s.startsWith('comparison.') || s.startsWith('comparison_') || s.startsWith('placement.recommendation.') || s.startsWith('placement_recommendation')) return P.VERGLEICH;
  if (
    s.startsWith('customer.') || s.startsWith('customer_delivery_') ||
    s.startsWith('placement.decision.') || s.startsWith('placement_decision_') ||
    s === 'placement.promoted_to_policy' || s === 'placement_promote'
  ) {
    return P.ENTSCHEID;
  }
  if (s.startsWith('application.') || s.startsWith('application_')) return P.ANTRAG;
  if (s.startsWith('placement.') || s.startsWith('placement_')) return P.AUSSCHREIBUNG;

  return P.SONSTIGES;
}

export interface PlacementHistorieEintrag { id: string; quelle: 'aktivitaet' | 'audit'; zeitpunkt: string; text: string; event: string | null; action: string | null; entity_type: string | null; entity_id: string | null; actor_kind: 'human' | 'system'; actor_name: string | null; kontext: Record<string, string | number | boolean | null>; phase: PlacementHistoriePhase; phase_label: string }
export interface PlacementHistorieResponse { placement_id: string; placement_number: string; eintraege: PlacementHistorieEintrag[]; next_cursor: string | null; quelle: 'aktivitaet' | 'alle'; audit_hidden_reason: 'permission' | null }
export interface PlacementHistorieQuery { limit?: number; cursor?: string; quelle?: 'aktivitaet' | 'alle' }

// ── Abschluss (Tab 6 section) ───────────────────────────────────────────────────────

export interface PolicenkontrolleEintrag { id: string; kontroll_nr: number; is_aktuell: boolean; ausgeloest_durch: PolicenkontrolleAusloeser; ausgeloest_durch_label: string; match_art: PolicenkontrolleMatchArt; match_art_label: string; result: PlacementComparisonResult; zeilen: PolicenkontrolleZeile[]; abweichungen_count: number; unbekannt_count: number; vollstaendig: boolean; policy_status_bei_kontrolle: string; befund: PolicenkontrolleBefund; rueckmeldung: PolicenkontrolleRueckmeldung | null; klaerung: { status: KlaerungStatus; status_label: string; task_id: string | null; geklaert_at: string | null; geklaert_by_name: string | null; grund: string | null }; ausgefuehrt_at: string; ausgefuehrt_by_name: string | null }
export interface PlacementAbschlussResponse {
  placement_id: string; placement_number: string; status: PlacementStatus; closure: { status: PlacementClosureStatus; start_zeitpunkt: string; end_zeitpunkt: string | null; abschluss_art: PlacementAbschlussArt | null; abschluss_art_label: string | null; abgeschlossen_by_name: string | null; comparison_result: PlacementComparisonResult | null; comparison_documented_at: string | null; dauer_tage: number | null };
  antrag: { id: string; status: ApplicationStatus; status_label: string; carrier_uid: string; carrier_name: string | null; submission_number: string; police_erhalten_policy_number: string | null; police_erhalten_at: string | null } | null;
  police: { id: string; policy_number: string; status: string; status_label: string; bezeichnung: string | null; carrier_uid: string | null; carrier_name: string | null; inception_date: string | null; expiry_date: string | null; premium_gross: number | null; aktiv: boolean; ersetzt_shell_id: string | null } | null;
  kontrolle: PolicenkontrolleEintrag | null; kontrollen: PolicenkontrolleEintrag[];
  zustellung: { zugestellt_at: string | null; channel: 'portal' | 'email' | null; channel_label: string | null; portal_delivery_id: string | null; communication_id: string | null; task_id: string | null; versand_laeuft: boolean; versand_unterbrochen: boolean; versand_gesperrt: boolean; nachtragen_moeglich: boolean; letzter_fehler: string | null; letzter_fehler_at: string | null; portal_verfuegbar: boolean; kunde_email: string | null };
  aktionen: { kontrolle_ausfuehren: boolean; police_verknuepfen: boolean; abweichung_klaeren: boolean; police_zustellen: boolean; rueckmeldung_versicherer: boolean };
  /** The server's recipient-ladder result for the Rueckmeldung an den Versicherer (editable in the dialog). */
  rueckmeldung_empfaenger_vorschlag: string | null;
  hinweise: string[];
}
export interface PkPoliceVerknuepfenBody { policy_number: string }
export interface PkAbweichungGeklaertBody { grund: string }
/** PR #43 wave 2 (#6): resolve an interrupted Police-Zustellung (record_failed). */
export interface PkZustellungAufloesenBody { aktion: 'nachtragen' | 'freigeben' }
export interface PkRueckmeldungVersichererBody { text: string; to?: string; in_reply_to_communication_id?: string }

// ── Befund (the human-readable finding) + the Rueckmeldung an den Versicherer (P2 hardening 05) ───────

/** The last Rueckmeldung the broker sent to the insurer on an abweichung (the control row stamps it). */
export interface PolicenkontrolleRueckmeldung { gesendet_at: string; empfaenger: string; communication_id: string; gesendet_by_name: string | null }

export interface PolicenkontrolleBefund {
  result: PlacementComparisonResult;
  /** One line: «Police HEL-2027-004711 weicht vom Antrag ab (1 Feld)» / «Police HEL-... stimmt mit dem Antrag ueberein». */
  headline: string;
  /** One per abweichend field: «Abweichung im Bereich Selbstbehalt: Antrag CHF 500, Police CHF 1'000». */
  abweichungen: string[];
  /** One per unbekannt field: «Versicherungssumme: in der Police nicht pruefbar» (never an Abweichung, Q144). */
  nicht_pruefbar: string[];
}

export const PK_MONEY_FELDER: readonly PolicenkontrolleFeld[] = ['praemie_jahr', 'versicherungssumme', 'selbstbehalt'];
export const PK_DATE_FELDER: readonly PolicenkontrolleFeld[] = ['versicherungsbeginn', 'vertragsablauf'];

/** Thousands grouping with an ASCII apostrophe: 1000 -> "1'000", 1234567 -> "1'234'567". */
function groupThousandsCh(intStr: string): string {
  return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
}

/** 1000 -> "CHF 1'000", 4850.5 -> "CHF 4'850.50", -1000 -> "CHF -1'000". Manual (no Intl, deterministic). */
function formatChf(n: number): string {
  const neg = n < 0;
  const abs = Math.abs(n);
  const cents = Math.round(abs * 100);
  const intPart = Math.floor(cents / 100);
  const frac = cents % 100;
  const intStr = groupThousandsCh(String(intPart));
  const body = frac === 0 ? intStr : `${intStr}.${String(frac).padStart(2, '0')}`;
  return `CHF ${neg ? '-' : ''}${body}`;
}

/**
 * Render one Kontrollfeld value for the Befund: money as «CHF 1'000» / «CHF 4'850.50», dates as
 * «DD.MM.YYYY», versicherer as the carrier name (fallback the raw UID), everything else as-is; null -> «-».
 *
 * `carrierName` resolves ONLY the carrier it names — the police's `carrierUid`. On a soll!=ist
 * versicherer deviation ("Antrag Helvetia, Police Zurich") the Antrag's UID must stay its raw UID and
 * NOT be relabelled to the police's carrier name. When `carrierUid` is not threaded in, carrierName is
 * applied unconditionally (back-compat for callers that only have the one carrier).
 */
export function formatPolicenkontrolleWert(feld: PolicenkontrolleFeld, wert: string | number | null, opts?: { carrierName?: string | null; carrierUid?: string | null }): string {
  if (wert === null || wert === undefined || wert === '') return '-';
  if (feld === 'versicherer') {
    if (opts?.carrierName && (opts.carrierUid == null || String(wert) === opts.carrierUid)) return opts.carrierName;
    return String(wert);
  }
  if (PK_MONEY_FELDER.includes(feld)) {
    const n = typeof wert === 'number' ? wert : Number(String(wert).replace(/chf/gi, '').replace(/['\s]/g, ''));
    return Number.isFinite(n) ? formatChf(n) : String(wert);
  }
  if (PK_DATE_FELDER.includes(feld)) {
    const m = String(wert).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}.${m[2]}.${m[1]}` : String(wert);
  }
  return String(wert);
}

export const PK_BEFUND_DECKUNGSGLEICH = (nr: string): string => `Police ${nr} stimmt mit dem Antrag ueberein`;
export const PK_BEFUND_ABWEICHUNG = (nr: string, n: number): string => `Police ${nr} weicht vom Antrag ab (${n} ${n === 1 ? 'Feld' : 'Felder'})`;
export const PK_ABWEICHUNG_ZEILE = (label: string, soll: string, ist: string): string => `Abweichung im Bereich ${label}: Antrag ${soll}, Police ${ist}`;
export const PK_NICHT_PRUEFBAR_ZEILE = (label: string): string => `${label}: in der Police nicht pruefbar`;
export const PK_SYSTEMANALYSE_TITLE = 'Systemanalyse';
export const PK_SYSTEMANALYSE_HINWEIS = 'Automatische Policenkontrolle gegen den Antrag der Ausschreibung';
export const PK_RUECKMELDUNG_MAIL_SUBJECT = (nr: string, submission: string): string => `Police ${nr} - Abweichung zum Antrag (${submission})`;
export const PK_RUECKMELDUNG_GESENDET_HINWEIS = 'Rueckmeldung an den Versicherer gesendet';
export const PK_RUECKMELDUNG_BESTAETIGUNG = 'Die Rueckmeldung wird aus Ihrem verbundenen Postfach an den Versicherer gesendet und auf der Ausschreibung dokumentiert.';
export const PK_RUECKMELDUNG_ERNEUT_HINWEIS = 'Zu dieser Kontrolle wurde bereits eine Rueckmeldung gesendet';
export const PK_RUECKMELDUNG_TEXT_MIN = 10;
export const PK_RUECKMELDUNG_TEXT_MAX = 5000;

/** result-agnostic: build the Befund (headline + per-field abweichung lines + nicht-pruefbar lines). */
export function policenkontrolleBefund(input: { policy_number: string; result: PlacementComparisonResult; zeilen: PolicenkontrolleZeile[]; carrier_name?: string | null; carrier_uid?: string | null }): PolicenkontrolleBefund {
  const abw = input.zeilen.filter((z) => z.status === 'abweichend');
  const unb = input.zeilen.filter((z) => z.status === 'unbekannt');
  // `carrier_name` belongs to the police's carrier (the versicherer row's `ist`). Thread its UID in
  // so a soll!=ist versicherer deviation shows the Antrag's raw UID, not the police's carrier name.
  const versichererZeile = input.zeilen.find((z) => z.feld === 'versicherer');
  const carrierUid = input.carrier_uid ?? (typeof versichererZeile?.ist === 'string' ? versichererZeile.ist : null);
  const headline = input.result === 'abweichung' ? PK_BEFUND_ABWEICHUNG(input.policy_number, abw.length) : PK_BEFUND_DECKUNGSGLEICH(input.policy_number);
  const abweichungen = abw.map((z) =>
    PK_ABWEICHUNG_ZEILE(z.label, formatPolicenkontrolleWert(z.feld, z.soll, { carrierName: input.carrier_name, carrierUid }), formatPolicenkontrolleWert(z.feld, z.ist, { carrierName: input.carrier_name, carrierUid })),
  );
  const nicht_pruefbar = unb.map((z) => PK_NICHT_PRUEFBAR_ZEILE(z.label));
  return { result: input.result, headline, abweichungen, nicht_pruefbar };
}

/** The default body the Rueckmeldung dialog is prefilled with (pure; the api wraps it in the mail builder). */
export function buildPkRueckmeldungText(input: { versicherer_name: string; kunde_name: string; policy_number: string; submission_number: string | null; befund: PolicenkontrolleBefund }): string {
  const subPart = input.submission_number ? ` (Offertanfrage ${input.submission_number})` : '';
  const zeilen = input.befund.abweichungen.length > 0 ? input.befund.abweichungen : [input.befund.headline];
  return [
    'Guten Tag',
    '',
    `zur Police ${input.policy_number} fuer ${input.kunde_name}${subPart} haben wir bei der Kontrolle gegen den eingereichten Antrag folgende Abweichung(en) festgestellt:`,
    '',
    ...zeilen.map((z) => `- ${z}`),
    '',
    'Bitte stellen Sie uns eine korrigierte Police gemaess Antrag zu.',
    '',
    'Freundliche Gruesse',
  ].join('\n');
}

// ── Posteingang view of the Kontrolle (the Maske pane) ─────────────────────────────

export interface PosteingangPolicenkontrolleEintrag {
  placement_id: string; placement_number: string; placement_status: PlacementStatus; closure_status: PlacementClosureStatus;
  policy_id: string; policy_number: string; carrier_name: string | null;
  kontrolle: PolicenkontrolleEintrag;
  aktionen: { abweichung_klaeren: boolean; rueckmeldung_versicherer: boolean };
  empfaenger_vorschlag: string | null;
  eingang_task_id: string | null;
}
export interface PosteingangPolicenkontrolleResponse { data: PosteingangPolicenkontrolleEintrag[] }

// ── Pinned user-facing strings (ASCII hyphens) ──────────────────────────────────────

export const PK_IMPLIZIT_P1 = 'Implizit - Police ueber den Policeneingang eingetroffen';
export const PK_ZUSTELLUNG_PORTAL_TITLE = (nr: string): string => `Ihre neue Police ${nr}`;
export const PK_ZUSTELLUNG_MAIL_SUBJECT = (nr: string, sparte: string): string => `Ihre Police ${nr} - ${sparte}`;
export const PK_KEINE_POLICE_HINWEIS = 'Noch keine Police verknuepft - sie wird ueber den Policeneingang automatisch zugeordnet oder kann hier manuell verknuepft werden';
export const PK_WARTET_AKTIVIERUNG_HINWEIS = 'Die Police ist noch im Entwurf - der Abschluss folgt mit ihrer Aktivierung im Policeneingang';
export const PK_WARTET_KLAERUNG_HINWEIS = 'Abweichung zwischen Police und Antrag - der Abschluss folgt nach der Klaerung mit dem Versicherer';
export const PK_DECKUNGSGLEICH_HINWEIS = 'Police und Antrag stimmen ueberein';
export const PK_UNVOLLSTAENDIG_HINWEIS = 'Nicht alle Felder konnten geprueft werden - fehlende Werte in der Police';
export const PK_ERLEDIGT_HINWEIS = 'Die Ausschreibung ist erledigt und fuer weitere Bearbeitung gesperrt - Folgeaktivitaeten als neues Placement anlegen';
export const PK_ZUSTELLUNG_MANUELL_HINWEIS = 'Kein aktiver Portalzugang - bitte die Police per E-Mail an den Kunden zustellen';
export const PK_ZUSTELLUNG_PORTAL_HINWEIS = 'Police im Kundenportal zugestellt';
export const PK_ZUSTELLUNG_EMAIL_HINWEIS = 'Police per E-Mail zugestellt';
export const PK_ZUSTELLUNG_FEHLER_HINWEIS = 'Zustellung fehlgeschlagen - bitte erneut versuchen';
export const PK_ZUSTELLUNG_UNTERBROCHEN_HINWEIS = 'Zustellung unterbrochen: E-Mail gesendet, aber nicht erfasst. Bitte im Postfach pruefen und aufloesen.';
/** PR #43 wave 2 (#6): the fresh-send lock window for the Police-Zustellung (minutes). */
export const PK_ZUSTELLUNG_VERSAND_LOCK_MINUTES = 10;
export const PK_KLAERUNG_BESTAETIGUNG = 'Mit der Klaerung wird die Abweichung als mit dem Versicherer bereinigt dokumentiert und die Ausschreibung freigegeben.';
export const PK_HISTORIE_LEER = 'Noch keine Ereignisse.';
export const PK_HISTORIE_TECHNISCH_LABEL = 'Technische Eintraege anzeigen';
